import axios from "axios";
import { authApi } from "./auth.api";
import { useAuthStore } from "../store/auth.store";

const STORAGE_KEY = "pmd-pro-frontend-auth";
const REFRESH_AHEAD = 2 * 60 * 1000;
const ACTIVE_WINDOW = 15 * 60 * 1000;
let lastActivity = Date.now();
let retryAfter = 0;
let pending: Promise<void> | null = null;

function storedSession() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null")?.state;
  } catch {
    return undefined;
  }
}

/** Ne synchronise que les jetons : chaque onglet conserve son école courante. */
function syncTokens() {
  const saved = storedSession();
  const current = useAuthStore.getState();
  if (!saved || saved.user?.id !== current.user?.id) return;
  if (
    saved.token !== current.token ||
    saved.refreshToken !== current.refreshToken
  ) {
    useAuthStore.setState({
      token: saved.token,
      refreshToken: saved.refreshToken,
      tokenExpiresAt: saved.tokenExpiresAt,
    });
  }
}

export function samePermioSession(a: string | null, b: string | null) {
  if (a === b) return true;
  try {
    const payload = (token: string) =>
      JSON.parse(
        atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
      );
    const first = payload(a!),
      second = payload(b!);
    return (
      typeof first.sid === "string" &&
      first.sid === second.sid &&
      first.sub === second.sub &&
      first.av === second.av
    );
  } catch {
    return false;
  }
}

/** Une rotation partagée par les requêtes et, avec Web Locks, par les onglets. */
export function renewPermioSession(rejectedToken?: string): Promise<void> {
  if (pending) return pending;
  const rotate = async () => {
    syncTokens();
    const initial = useAuthStore.getState();
    if (!initial.token || !initial.refreshToken) return;
    // Un autre onglet ou une requête précédente a déjà remplacé le jeton refusé.
    if (rejectedToken && initial.token !== rejectedToken) return;
    if (
      !rejectedToken &&
      (!initial.tokenExpiresAt ||
        initial.tokenExpiresAt > Date.now() + REFRESH_AHEAD)
    )
      return;
    try {
      const tokens = await authApi.refresh(initial.refreshToken);
      const current = useAuthStore.getState();
      const saved = storedSession();
      if (
        current.refreshToken !== initial.refreshToken ||
        saved?.refreshToken !== initial.refreshToken
      )
        return;
      useAuthStore.setState({
        token: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        tokenExpiresAt: Date.now() + tokens.expiresIn * 1000,
      });
      retryAfter = 0;
    } catch (error) {
      retryAfter = Date.now() + 30000;
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 401 &&
        useAuthStore.getState().refreshToken === initial.refreshToken &&
        storedSession()?.refreshToken === initial.refreshToken
      ) {
        await useAuthStore.getState().logout();
      }
      throw error;
    }
  };
  pending = (async () => {
    if (navigator.locks)
      await navigator.locks.request("permio-session-refresh", rotate);
    else await rotate();
  })().finally(() => {
    pending = null;
  });
  return pending;
}

export function sessionRecentlyActive() {
  return Date.now() - lastActivity <= ACTIVE_WINDOW;
}

export async function renewActiveSession() {
  if (
    document.visibilityState !== "visible" ||
    !sessionRecentlyActive() ||
    Date.now() < retryAfter
  )
    return;
  const current = useAuthStore.getState();
  if (
    !current.token ||
    !current.refreshToken ||
    !current.tokenExpiresAt ||
    current.tokenExpiresAt > Date.now() + REFRESH_AHEAD
  )
    return;
  await renewPermioSession();
}

export function maintainPermioSession() {
  const tick = () => {
    void renewActiveSession().catch(() => undefined);
  };
  const activity = () => {
    lastActivity = Date.now();
    tick();
  };
  const visible = () => {
    if (document.visibilityState === "visible") activity();
  };
  const storage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY) return;
    const saved = storedSession();
    const current = useAuthStore.getState();
    if (!saved?.token && current.token) {
      useAuthStore.setState({
        token: null,
        refreshToken: null,
        tokenExpiresAt: null,
        user: null,
        school: null,
        activeSchoolId: null,
        availableSchools: [],
      });
    } else if (saved?.user?.id !== current.user?.id) {
      window.location.reload();
    } else syncTokens();
  };
  const events = ["pointerdown", "keydown", "scroll"] as const;
  events.forEach((name) =>
    window.addEventListener(name, activity, { passive: true }),
  );
  document.addEventListener("visibilitychange", visible);
  window.addEventListener("online", activity);
  window.addEventListener("storage", storage);
  const timer = window.setInterval(tick, 30000);
  return () => {
    clearInterval(timer);
    events.forEach((name) => window.removeEventListener(name, activity));
    document.removeEventListener("visibilitychange", visible);
    window.removeEventListener("online", activity);
    window.removeEventListener("storage", storage);
  };
}
